import React, { FC, useCallback, useContext, useMemo } from 'react';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowData, GridRowParams } from '@mui/x-data-grid';
import { useLocation } from 'react-router-dom';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { DateFormat } from '../../helpers/dateFormat';
import { ProductsFetcher } from '../../fetchers/ProductsFetcher';
import { useAutoFetch } from '../../contexts/Fetcher';
import { AddProductDialog } from './AddProductDialog';
import { EditProductDialog } from './EditProductDialog';
import { useDefaultSorting } from '../../hooks/useDefaultSorting';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../../hooks/useDialog';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import { useQueryParam } from '../../hooks/useQueryParam';
import { apiProduct } from '../../resources/api/apiProduct';
import { useResourceChecker } from '../../resources/resource';
import { queryUrl } from '../../hooks/useQueryUrl';
import { CollapsedContentsWithTooltip } from '../CollapsedContentsWithTooltip/CollapsedContentsWithTooltip';
import { IAdminProductPackageSummary } from '../../models/ProductsResponse';

const { useUpdateProduct } = apiProduct;

export const ProductsList: FC = () => {
  const { data } = useContext(ProductsFetcher.Context);
  useAutoFetch(ProductsFetcher);
  const { search } = useLocation();

  const { isOpen: isAdding, close: closeDialog } = useDialog('new');

  const abortEdit = useCloseDialog(void 0, ['edit']);

  const id = useQueryParam('edit');
  const { resource: resourceUpdate } = useUpdateProduct('');
  const resourceChecker = useResourceChecker();

  useDefaultSorting('legacyId', 'asc');
  const { t } = useTranslation();

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'legacyId',
        headerName: t('common.tableHeader.id'),
      },
      {
        field: 'name',
        headerName: t('common.tableHeader.name'),
        flex: 1,
      },
      {
        field: 'packagesCount',
        headerName: t('adminProducts.products.table.packagesHeader'),
        flex: 1,
        valueGetter: (params) => {
          const packages = (params.row.packages as IAdminProductPackageSummary[] | undefined) ?? [];
          return t('adminProducts.products.table.totalPackages', { count: packages.length });
        },
        renderCell: (params) => {
          const packages = (params.row.packages as IAdminProductPackageSummary[] | undefined) ?? [];
          const items = packages.map((pack) => ({
            name: pack.name,
          }));
          return <CollapsedContentsWithTooltip items={items}>{params.value}</CollapsedContentsWithTooltip>;
        },
      },
      {
        field: 'created',
        headerName: t('common.tableHeader.created'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 130,
      },
      {
        field: 'updated',
        headerName: t('common.tableHeader.updated'),
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
        width: 130,
      },
    ],
    []
  );

  const rows = useMemo<GridRowData[]>(() => {
    if (!data) return [];
    return data.products.map((product) => ({
      id: product.id,
      legacyId: product.legacyId,
      name: product.name,
      created: product.created,
      updated: product.updated,
      packages: product.packages ?? [],
    }));
  }, [data]);

  const getRowLink = useCallback((params: GridRowParams) => queryUrl('edit', params.row.id, search), [search]);

  const isGetRowLinkAllowed = resourceChecker(resourceUpdate);

  if (!data) return null;
  const product = id ? data.products.find((p) => p.id === id) : undefined;

  return (
    <div>
      <UrlQueryDataGrid
        columns={columns}
        rows={rows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        getRowLink={isGetRowLinkAllowed ? getRowLink : void 0}
      />

      <AddProductDialog open={isAdding} closeDialog={closeDialog} />
      <EditProductDialog product={product} closeDialog={abortEdit} />
    </div>
  );
};
